import { Box, IconButton, styled, Tooltip } from '@mui/material';
import { history } from 'app';
import Elevio from 'elevio/lib/client';
import Sidebar, {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from 'new/components/AppBar/Sidebar';
import LocationHeader, {
  LocationHeaderProps,
  useInitSingleLocationSelector,
} from 'new/components/LocationHeader';
import PopperMenu from 'new/components/PopperMenu';
import SearchMembers from 'new/components/SearchMembers';
import { boxShadow } from 'new/constants/styles';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { useInternalPatientsStore } from 'new/modules/InternalPatientSignup/hooks';
import findRoute from 'new/routing/routes';
import React, { ReactNode } from 'react';
import { Link } from 'react-router';
import Breadcrumbs from './Breadcrumbs';
import EnrollHeader, {
  BREADCRUMBS_HEIGHT,
  TOPBAR_HEIGHT,
} from './Headers/EnrollHeader';
import SetupHeader from './Headers/SetupHeader';
import IconFontAwesome from './IconFontAwesome';
import ReleaseNotesButton from './ReleaseNotesButton';
import { TENANT } from 'new/tenants';

Elevio.load(TENANT.constants.elevioId).then((res) => {
  res.setSettings({
    hideLauncher: true,
    reinitializeOnUrlChange: false,
    enabled: true,
  });
});

type Props = {
  topBar?: 'setup' | 'enroll';
  showSidebar?: boolean;
  orgName?: string;
  isExpanded: boolean;
  breadcrumbs?: ReactNode[];
  locationHeader?: LocationHeaderProps;
  maxWidth?: string;
};

const TopBarBox = styled(Box, {
  shouldForwardProp: (prop) =>
    !['hasBreadcrumbs', 'hasTopbar'].includes(prop as string),
})<{
  hasBreadcrumbs: boolean;
  hasTopbar: boolean;
}>(({ hasBreadcrumbs, hasTopbar }) => {
  let height = 0;

  if (hasTopbar) {
    height = TOPBAR_HEIGHT;
  }

  if (hasBreadcrumbs) {
    height = BREADCRUMBS_HEIGHT;
  }

  return {
    width: '100%',
    height,
    float: 'right',
    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  };
});

const PersistentIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  zIndex: 3,
  right: theme.spacing(5),
  gap: theme.spacing(2),
  minHeight: 45,

  '> div': {
    cursor: 'pointer',
    padding: theme.spacing(1),
  },

  '.popper': {
    marginRight: `${theme.spacing(3)} !important`,
  },

  '.popperMenu': {
    padding: 1,
  },

  '&.return-dashboard': {
    color: '#777700',
  },
}));

const AddMemberIcon = styled(Link)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
  boxShadow,

  '> div': {
    padding: 12,
  },

  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    color: '#fff',
    cursor: 'pointer',
  },
}));

const internalSignupRoute = findRoute('InternalPatientSignup');

const PageLayout: React.FC<Props> = ({
  topBar,
  orgName = '',
  showSidebar,
  children,
  isExpanded,
  breadcrumbs,
  locationHeader,
  maxWidth = '1920px',
}) => {
  const { resetPatientStore } = useInternalPatientsStore();
  const sidebarWidth =
    showSidebar &&
    (isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH);
  useInitSingleLocationSelector();
  const { user, activeOfficeId } = useActiveOffice();
  const upgradedOffice = user?.data.tieredPayment?.tierLevel != 0;

  const renderHeader = () => {
    if (locationHeader) {
      return <LocationHeader {...locationHeader} />;
    }

    if (breadcrumbs) {
      return <Breadcrumbs breadcrumbs={breadcrumbs} />;
    }

    if (topBar === 'setup') {
      return <SetupHeader topTitle={orgName} bottomTitle="SET UP ACCOUNT" />;
    }

    if (topBar === 'enroll') {
      return <EnrollHeader />;
    }

    return null;
  };

  return (
    <Box maxWidth={topBar ? 'unset' : maxWidth} position="relative">
      {(locationHeader?.addMemberIcon ||
        locationHeader?.hasGlobalSearch ||
        locationHeader?.hasGlobalHelp) && (
        <PersistentIconContainer>
          {(locationHeader?.addMemberIcon ||
            locationHeader?.hasGlobalSearch) && (
            <PopperMenu
              id="search-members"
              placement="left"
              popperContent={(onClose) => <SearchMembers onClose={onClose} />}
              hasBackdrop={false}
            >
              {({ id, onClick }) => (
                <Tooltip title="Search">
                  <IconButton id={id} onClick={onClick} disabled={false}>
                    <IconFontAwesome
                      name="faMagnifyingGlass"
                      color="grey400"
                      size="sm"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </PopperMenu>
          )}
          {(locationHeader?.addMemberIcon || locationHeader?.hasGlobalHelp) && (
            <Tooltip title="Help">
              <IconButton onClick={() => Elevio.openHome()} disabled={false}>
                <IconFontAwesome
                  name="faCircleQuestion"
                  color="grey400"
                  size="sm"
                />
              </IconButton>
            </Tooltip>
          )}
          {window.location.href.indexOf('/account') != -1 && (
            <ReleaseNotesButton activeOfficeId={activeOfficeId} />
          )}
          {locationHeader?.addMemberIcon && (
            <AddMemberIcon
              onClick={() => resetPatientStore()}
              to={internalSignupRoute.path as string}
            >
              <Tooltip title="Add Member">
                <Box>
                  <IconFontAwesome name="faUserPlusSolid" isCenter size="lg" />
                </Box>
              </Tooltip>
            </AddMemberIcon>
          )}
        </PersistentIconContainer>
      )}
      {!upgradedOffice && (
        <PersistentIconContainer className="return-dashboard">
          <Tooltip title="Go Back">
            <IconButton onClick={() => history.push('/dentist-new/members')}>
              RETURN TO DASHBOARD &emsp;
              <IconFontAwesome name="faArrowRight" color="grey400" size="sm" />
            </IconButton>
          </Tooltip>
        </PersistentIconContainer>
      )}
      <TopBarBox
        sx={{ width: `calc(100% - ${sidebarWidth}px)` }}
        hasBreadcrumbs={!!breadcrumbs}
        hasTopbar={!!topBar}
      >
        {renderHeader()}
      </TopBarBox>
      {showSidebar && <Sidebar />}
      {children}
    </Box>
  );
};

export { PageLayout };
